import { UitkCardAloha, UitkCardAlohaLink, UitkCardAlohaView, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkPrimaryButton } from "uitk-react-button";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkExpandoLink, UitkExpandoLinkTypeSize } from "uitk-react-expando";
import { UitkText } from "uitk-react-text";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import * as React from "react";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { LobSummaryProps, PackageCardProps } from "./typings";
import DescriptionAndGuestReviewSection from "./components/DescriptionAndGuestReviewSection";
import PricesAndDate from "./components/PricesAndDate";
import { ReviewCountAndRating } from "components/flexComponents/Hotels/components/ReviewCountAndRating";
import SavingBadge from "./components/SavingBadge";
import { CardTitle } from "./components/CardTitle";
import { HotelRating } from "./components/HotelRating";
import { AddressAndNeighborhood } from "./components/AddressAndNeighborhood";
import LobDetails from "./components/LobDetails";
import { useLocalization } from "@shared-ui/localization-context";
import { getFormattedHotelDates } from "./utils/PackageDates";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import LobBadge from "./components/LobBadge";
import { AnalyticsConfig } from "uitk-react-utils";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";

export const PackageCardImageLeftMobile = ({ packageItem, index, language }: PackageCardProps) => {
  const TrackedButton = FlexClickTracker(UitkPrimaryButton);
  const { formatText } = useLocalization();
  const packageType = "fh";
  const track = useClickTracker();
  const expandoLinkAnalytics: AnalyticsConfig = {
    id: `Packages.${index.toString()}.ImageLeftExpando.ExpandoLink`,
    callback: (rfrr) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: "packaging",
        rfrr,
        action: Action.CLICK,
      };
      sendDelayedClickstreamTrackEvent(flexTrackingInfo, track);
    },
  };

  return (
    <UitkCardAloha className="all-t-margin-three">
      <UitkCardAloha padded={true}>
        <UitkCardAlohaView layout={{ image_fixed: 80 }} className="truncate">
          <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
            <UitkImage src={packageItem.hotelImageUrl} alt={packageItem.hotelName} placeholderImage={true} />
          </UitkFigure>
          <UitkCardAlohaContentSection padded={false}>
            <UitkLayoutFlex justifyContent="space-between" className="truncate">
              <UitkLayoutFlexItem className="truncate">
                <div>
                  <HotelRating rating={packageItem.hotelStarRating} isText={true} />
                  <CardTitle cardTitle={packageItem.hotelName} />
                  <AddressAndNeighborhood packageItem={packageItem} />
                  {packageItem.showHotelGuestRating && (
                    <ReviewCountAndRating
                      hotelOverallReviewRating={Number(packageItem.hotelReviewScore)}
                      reviewCount={Number(packageItem.hotelReviewTotal)}
                      language={language}
                    />
                  )}
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkCardAlohaContentSection>
        </UitkCardAlohaView>
        <UitkCardAlohaView>
          <UitkCardAlohaContentSection padded={false}>
            <UitkLayoutFlex direction="row-reverse">
              <SavingBadge markers={packageItem.markers} />
            </UitkLayoutFlex>
            <UitkLayoutFlex justifyContent="space-between" alignItems="end">
              <UitkLayoutFlexItem className="s-r-padding-six">
                <div>
                  <LobBadge packageType={packageType} />
                  <LobSummary packageItem={packageItem} packageType={packageType} />
                </div>
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem shrink={0}>
                <div>
                  <PricesAndDate packageItem={packageItem} renderSpace={true} />
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkCardAlohaContentSection>
        </UitkCardAlohaView>
        <UitkCardAlohaLink>
          <TrackedLink
            className="uitk-card-link"
            rfrr={`card-${index}`}
            href={packageItem.shopUrl}
            aria-label={formatText("package.card.ariaLabel.fh")}
            moduleName="packages"
          />
        </UitkCardAlohaLink>
      </UitkCardAloha>
      <UitkCardAloha>
        <UitkCardAlohaView>
          <UitkCardAlohaContentSection border="top" padded={false} className="all-x-margin-three">
            <UitkExpandoLink
              type={UitkExpandoLinkTypeSize.TYPE_200}
              expandoTitle={formatText("recentSearches.mobile.collapsedButton")}
              expandedLabel={formatText("recentSearches.mobile.expandedButton")}
              isVisible={false}
              analytics={expandoLinkAnalytics}
            >
              <UitkCardAlohaContentSection padded={false} className="all-b-padding-three">
                <LobDetails packageItem={packageItem} />
              </UitkCardAlohaContentSection>
              <UitkCardAlohaContentSection border="top" padded={false} className="all-t-padding-three">
                <DescriptionAndGuestReviewSection
                  packageItem={packageItem}
                  language={language}
                  renderReviewRating={false}
                />
                <TrackedButton
                  isFullWidth={true}
                  moduleName="packages"
                  rfrr={`card-${index}.select.`}
                  href={packageItem.shopUrl}
                >
                  {formatText("hotelsViews.expando.select")}
                </TrackedButton>
              </UitkCardAlohaContentSection>
            </UitkExpandoLink>
          </UitkCardAlohaContentSection>
        </UitkCardAlohaView>
      </UitkCardAloha>
    </UitkCardAloha>
  );
};

const LobSummary: React.FC<LobSummaryProps> = ({ packageItem, packageType }): React.ReactElement | null => {
  const { formatText } = useLocalization();
  const locPath = "package.card.fragment";
  if (packageType === "fh") {
    const flightFragment = formatText(`${locPath}.roundtrip${packageItem.nonStop ? "NonStop" : ""}Flight`);
    const hotelFragment = formatText(
      `${locPath}.hotelNightStay.${packageItem.numberOfNights > 1 ? "multiple" : "single"}`,
      packageItem.numberOfNights
    );

    return (
      <UitkText size={200} spacing="half">
        <UitkText size={200} inline={true}>
          {formatText("package.card.fragment.sentence", flightFragment, hotelFragment)}
        </UitkText>
        <UitkText size={200} weight="bold" inline={true} className="all-l-margin-one">
          {getFormattedHotelDates({
            checkInDate: packageItem.isoCheckInDate,
            checkOutDate: packageItem.isoCheckOutDate,
          })}
        </UitkText>
      </UitkText>
    );
  }

  return null;
};
